import { Text, Field, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import Link, { LinkVariant } from 'components/helpers/Link/Link';
import Button from 'components/helpers/Button/Button';
import { DateRangeFields, DateRangeProps, defaultLocation, emptyLocation, LocationProps } from './Location.Types';
import DateRange from './DateRange';
import { useState } from 'react';

import styles from './LocationsListing.module.scss';

const Location = (props: LocationProps): React.JSX.Element => {
  const { id } = props;
  const location = props.fields;

  const { sitecoreContext } = useSitecoreContext();
  const defaultOpen = sitecoreContext?.pageEditing ?? false;
  const [viewMore, setViewMore] = useState(defaultOpen);

  if (location.tty.value !== '' && !location.tty.value.startsWith(' (')) {
    location.tty.value = ` (TTY ${location.tty.value}) `;
  }

  if (location.city.value !== '' && !location.city.value.endsWith(', ')) {
    location.city.value = `${location.city.value}, `;
  }

  const buttonMoreId = `viewMore-button-${id}`;
  const contentId = `viewMore-content-${id}`;

  const onClick: React.MouseEventHandler = () => {
    setViewMore(!viewMore);
  };

  const ranges = Array<DateRangeProps>();
  const today = new Date(new Date().setHours(12, 0, 0, 0));
  const thisYear = today.getFullYear();
  let currentRange = emptyLocation;

  function getDateNoon(date: Field<Date> | null, start: boolean) {
    //Dates were sometimes a day off when formatted and the associated timezone changed. Setting the time to noon avoided this issue
    const month: number = start ? 1 : 11;
    const day: number = start ? 1 : 31;
    const returnDate: Date = date
      ? new Date(new Date(new Date(date.value).setFullYear(thisYear)).setHours(12, 0, 0, 0))
      : new Date(thisYear, month, day, 12, 0, 0);
    return returnDate;
  }

  //If there are more than one date ranges used, find the one that is currently being used and seperate it from the rest
  if (location.activeDateRanges.length > 1) {
    const tempRanges: Array<DateRangeProps> = location.activeDateRanges;

    tempRanges.sort(function (a, b) {
      //#region for sorting purposes, set default dates for null entries
      const firstDateStart: Date = getDateNoon(a.fields.dateRangeStart, true);
      const firstDateEnd: Date = getDateNoon(a.fields.dateRangeEnd, false);
      const secondDateStart: Date = getDateNoon(b.fields.dateRangeStart, true);
      const secondDateEnd: Date = getDateNoon(b.fields.dateRangeEnd, false);
      //#endregion
      return firstDateEnd < secondDateEnd
        ? -1
        : firstDateEnd > secondDateEnd
        ? 1
        : firstDateStart > secondDateStart
        ? 1
        : firstDateStart < secondDateStart
        ? -1
        : 0;
    }); //sort by end date ascending then by start date ascending

    let currentMarker = 0;

    tempRanges.map((thisRange, count) => {
      if (currentRange?.name !== thisRange.name) {
        const thisStart = getDateNoon(thisRange.fields.dateRangeStart, true);
        const thisEnd = getDateNoon(thisRange.fields.dateRangeEnd, false);

        if (thisStart <= today && thisEnd >= today) {
          if (currentRange === undefined) {
            currentRange = thisRange;
            currentMarker = count;
          } //if the current range isn't set, go ahead and set it to thisRange
          else if (
            today.getTime() - thisStart.getTime() <
              today.getTime() - getDateNoon(currentRange.fields.dateRangeStart, true).getTime() ||
            (thisStart.getTime() === getDateNoon(currentRange.fields.dateRangeStart, true).getTime() &&
              thisEnd.getTime() < getDateNoon(currentRange.fields.dateRangeEnd, false).getTime())
          ) {
            currentRange = thisRange;
            currentMarker = count;
          } /* if thisRange's start date is closer to today than the currentRange's, or when start dates are the same but thisRange's end date is sooner 
              move what is currently in currentRange to the list of unused ranges, and set the active range as currentRange */
        } //if the current date falls between thisRange's start and end dates
      }
    });
    currentRange = tempRanges[currentMarker];

    if (currentMarker === 0) {
      tempRanges.slice(1).forEach((element) => ranges.push(element));
    } else {
      tempRanges
        .slice(currentMarker + 1)
        .concat(tempRanges.slice(0, currentMarker))
        .forEach((element) => ranges.push(element));
    }
  } else if (location.activeDateRanges.length === 1) {
    currentRange = location.activeDateRanges[0];
  } else {
    currentRange = defaultLocation;
  }

  return (
    <div className={styles['location']}>
      <Text className={styles['locationName']} field={location.name} tag="div" encode={sitecoreContext.pageEditing} />
      <div className={styles['addressTable']}>
        {location.buildingName.value !== '' && (
          <div className={styles['addressRow']}>
            {location.buildingName.value !== '' && <Text field={location.buildingName} tag="div" />}
          </div>
        )}
        {location.postalBox.value !== '' && (
          <div className={styles['addressRow']}>
            <Text field={location.postalBox} tag="div" />
          </div>
        )}
        <div className={styles['addressRow']}>
          <Text field={location.streetAddress1} tag="div" className={styles['addressCell']} />
          {location.streetAddress2.value !== '' && (
            <Text field={location.streetAddress2} tag="div" className={styles['addressCell']} />
          )}
        </div>
        <div className={styles['addressRow']}>
          <Text field={location.city} tag="div" className={styles['addressCell']} />
          <Text field={location.state} tag="div" className={styles['addressCell']} />
          <Text field={location.zip} tag="div" className={styles['addressCell']} />
        </div>
      </div>
      {location.directionsLink.value.href !== '' && (
        <Link
          className={styles['directions']}
          field={location.directionsLink}
          showLinkTextWithChildrenPresent={false}
          variant={LinkVariant.Inline}
          icon="external-link"
        >
          <Text field={location.directionsLabel} encode={sitecoreContext.pageEditing} />
        </Link>
      )}
      {location.phoneLink.value.href !== '' && (
        <div className={styles['phone']}>
          <Link
            className={styles['noBreak']}
            field={location.phoneLink}
            showLinkTextWithChildrenPresent={false}
            variant={LinkVariant.Inline}
          >
            {location.phoneLabel.value}
          </Link>
          {location.tty.value !== '' && (
            <Text className={styles['noBreak']} field={location.tty} encode={sitecoreContext.pageEditing} />
          )}
        </div>
      )}
      <div className={styles['hoursHeader']}>Hours</div>
      <DateRange name={currentRange.name} fields={currentRange.fields as DateRangeFields} active={true} />
      {props.fields.activeDateRanges.length > 1 && (
        <>
          <div hidden={!viewMore}>
            {ranges.map((range, i) => (
              <DateRange key={i} name={range.name} fields={range.fields as DateRangeFields} active={false} />
            ))}
          </div>
          <Button
            aria-controls={contentId}
            aria-expanded={viewMore}
            className={styles['viewButton']}
            icon={viewMore ? 'minus' : 'plus'}
            id={buttonMoreId}
            onClick={onClick}
            type="button"
          >
            {viewMore ? 'View Less Hours' : 'View More Hours'}
          </Button>
        </>
      )}
    </div>
  );
};

export default Location;
